/**
 * The POS cart.
 *
 * IMPORTANT: the cart holds NO PRICES that anything is charged from. It keeps
 * unit prices only so the cashier can read a running estimate while typing; the
 * authoritative total comes back from the API (`totals: OrderPricing`) the
 * moment the order is created, because tax, service charge, day-part pricing
 * and rounding are all server-side rules the client must never re-implement.
 *
 * A cart line is identified by a client UUID rather than by
 * product+variant+modifiers, because two lines of the same dish with different
 * notes ("không hành" vs "nhiều hành") are genuinely different lines and
 * merging them would send the wrong ticket to the kitchen.
 */

import type { LineInput, Modifier, ModifierGroup, Product, ProductVariant } from '../../lib/types';

export interface CartModifier {
  readonly modifierId: string;
  readonly modifierGroupId: string;
  readonly name: string;
  readonly priceDeltaMinor: number;
  readonly quantity: number;
}

export interface CartLine {
  readonly key: string;
  readonly productId: string;
  readonly productName: string;
  readonly variantId: string | null;
  readonly variantName: string | null;
  readonly unitPriceMinor: number;
  readonly quantity: number;
  readonly note: string | null;
  readonly spiceLevel: number | null;
  readonly courseNo: number;
  readonly modifiers: readonly CartModifier[];
}

export interface Cart {
  readonly lines: readonly CartLine[];
}

export const EMPTY_CART: Cart = { lines: [] };

let seq = 0;

function nextKey(): string {
  seq += 1;
  return `line-${Date.now().toString(36)}-${seq}`;
}

export function lineEstimateMinor(line: CartLine): number {
  const modifiers = line.modifiers.reduce((acc, m) => acc + m.priceDeltaMinor * m.quantity, 0);
  return (line.unitPriceMinor + modifiers) * line.quantity;
}

export function cartEstimateMinor(cart: Cart): number {
  return cart.lines.reduce((acc, line) => acc + lineEstimateMinor(line), 0);
}

export function cartItemCount(cart: Cart): number {
  return cart.lines.reduce((acc, line) => acc + line.quantity, 0);
}

export interface AddLineInput {
  readonly product: Product;
  readonly variant: ProductVariant | null;
  readonly modifiers: readonly CartModifier[];
  readonly quantity: number;
  readonly note: string | null;
  readonly spiceLevel: number | null;
  readonly courseNo?: number;
}

export function addLine(cart: Cart, input: AddLineInput): Cart {
  const line: CartLine = {
    key: nextKey(),
    productId: input.product.id,
    productName: input.product.nameVi,
    variantId: input.variant?.id ?? null,
    variantName: input.variant?.nameVi ?? null,
    // Base + variant delta. An ESTIMATE only — see the file header.
    unitPriceMinor: input.product.basePriceMinor + (input.variant?.priceDeltaMinor ?? 0),
    quantity: Math.max(1, Math.round(input.quantity)),
    note: input.note === null || input.note.trim() === '' ? null : input.note.trim(),
    spiceLevel: input.spiceLevel,
    courseNo: input.courseNo ?? 1,
    modifiers: input.modifiers,
  };
  return { lines: [...cart.lines, line] };
}

export function setQuantity(cart: Cart, key: string, quantity: number): Cart {
  if (quantity <= 0) return removeLine(cart, key);
  return {
    lines: cart.lines.map((line) => (line.key === key ? { ...line, quantity: Math.round(quantity) } : line)),
  };
}

export function setNote(cart: Cart, key: string, note: string | null): Cart {
  return {
    lines: cart.lines.map((line) =>
      line.key === key ? { ...line, note: note === null || note.trim() === '' ? null : note.trim() } : line,
    ),
  };
}

export function setSpice(cart: Cart, key: string, spiceLevel: number | null): Cart {
  return { lines: cart.lines.map((line) => (line.key === key ? { ...line, spiceLevel } : line)) };
}

export function removeLine(cart: Cart, key: string): Cart {
  return { lines: cart.lines.filter((line) => line.key !== key) };
}

/**
 * Cart → the API's `items` array.
 *
 * `clientUuid` is the cart line key, so a retried submission maps back to the
 * same line and the server can dedupe per line rather than per order.
 */
export function toLineInputs(cart: Cart): LineInput[] {
  return cart.lines.map((line) => ({
    productId: line.productId,
    variantId: line.variantId,
    quantity: line.quantity,
    noteVi: line.note,
    spiceLevel: line.spiceLevel,
    courseNo: line.courseNo,
    clientUuid: line.key,
    modifiers: line.modifiers.map((m) => ({
      modifierId: m.modifierId,
      modifierGroupId: m.modifierGroupId,
      quantity: m.quantity,
    })),
  }));
}

/**
 * Validate a modifier selection against the group's rules, honouring the
 * per-product override (`link`) which is what the API prices against.
 */
export interface GroupRules {
  readonly required: boolean;
  readonly minSelect: number;
  readonly maxSelect: number;
}

export function groupRules(group: ModifierGroup): GroupRules {
  return {
    required: group.link?.isRequiredOverride ?? group.isRequired,
    minSelect: group.link?.minSelectOverride ?? group.minSelect,
    maxSelect: group.link?.maxSelectOverride ?? group.maxSelect,
  };
}

/** Vietnamese complaint about a selection, or `null` when it is valid. */
export function validateGroup(group: ModifierGroup, selected: readonly CartModifier[]): string | null {
  const rules = groupRules(group);
  const count = selected.reduce((acc, m) => acc + m.quantity, 0);
  if (rules.required && count < Math.max(1, rules.minSelect)) {
    return `"${group.nameVi}" là bắt buộc, chọn tối thiểu ${Math.max(1, rules.minSelect)}.`;
  }
  if (count > 0 && count < rules.minSelect) {
    return `"${group.nameVi}" cần tối thiểu ${rules.minSelect} lựa chọn.`;
  }
  if (rules.maxSelect > 0 && count > rules.maxSelect) {
    return `"${group.nameVi}" chỉ cho phép tối đa ${rules.maxSelect} lựa chọn.`;
  }
  return null;
}

export function defaultSelection(groups: readonly ModifierGroup[]): CartModifier[] {
  const out: CartModifier[] = [];
  for (const group of groups) {
    for (const modifier of group.modifiers) {
      if (!modifier.isDefault || !modifier.isActive) continue;
      out.push(toCartModifier(group, modifier, 1));
    }
  }
  return out;
}

export function toCartModifier(group: ModifierGroup, modifier: Modifier, quantity: number): CartModifier {
  return {
    modifierId: modifier.id,
    modifierGroupId: group.id,
    name: modifier.nameVi,
    priceDeltaMinor: modifier.priceDeltaMinor,
    quantity,
  };
}
